/**
 * Estimated Delivery Date — client refresh (JoffzArt)
 * ---------------------------------------------------
 * The dates are already rendered server-side by Liquid, so there is
 * no flash and no layout shift. This tiny custom element only
 * *re-computes* the two dates in the visitor's storefront language,
 * which guarantees correct dates even when the page HTML is cached
 * (CDN / browser back-forward cache) or when the cart drawer is
 * re-rendered via Ajax (custom elements re-initialize automatically
 * whenever they are re-inserted into the DOM).
 *
 * No dependencies. No jQuery. ~0.6 KB gzipped.
 */
(() => {
  'use strict';

  if (customElements.get('estimated-delivery')) return;

  /** Add `days` business days (Mon–Fri) to `date`. */
  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    let remaining = days;
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay(); // 0 = Sun, 6 = Sat
      if (dow !== 0 && dow !== 6) remaining -= 1;
    }
    return d;
  };

  class EstimatedDelivery extends HTMLElement {
    connectedCallback() {
      // Defer one frame so we never block rendering.
      requestAnimationFrame(() => this.refresh());
    }

    refresh() {
      const min = parseInt(this.dataset.minDays, 10);
      const max = parseInt(this.dataset.maxDays, 10);
      const cutoff = parseInt(this.dataset.cutoff, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;

      const now = new Date();
      const extra = !Number.isNaN(cutoff) && now.getHours() >= cutoff ? 1 : 0;

      const dMin = addBusinessDays(now, min + extra);
      const dMax = addBusinessDays(now, max + extra);

      // Use the storefront language (set by Shopify on <html lang>).
      const locale = document.documentElement.lang || 'en';
      let fmt;
      try {
        fmt = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric' });
      } catch (_) {
        fmt = new Intl.DateTimeFormat('en', { month: 'long', day: 'numeric' });
      }

      const apply = (selector, date) => {
        const el = this.querySelector(selector);
        if (!el) return;
        const text = fmt.format(date);
        // Only touch the DOM when the value actually changed → no flicker.
        if (el.textContent !== text) el.textContent = text;
        el.setAttribute('datetime', date.toISOString().split('T')[0]);
      };

      apply('[data-edd-date="min"]', dMin);
      apply('[data-edd-date="max"]', dMax);
    }
  }

  customElements.define('estimated-delivery', EstimatedDelivery);
})();